// Mobile Google Business — 2026-09-14 v1.5 修正:
//   - 移除 location chip selector (mobile-filter-chip):
//     PC 侧才需要切店舗,Mobile 只看当前店(由 settings.currentLocation 决定)。
//   - AIインサイト:接入真实 API (googleBusinessApi.generateAiInsight),
//     复用 PC 版 normalizeAiInsight + buildInsight fallback。
//   - 保留 v1.4 的:3 段 AppBar / 删星级评价 / 編集 modal → PC / 4 KPI。
// 不调新增 API / 不写 AI / 不写编辑表单 / 不动 PC。
//
// 2026-09-16 v1.6:AIインサイト loading UX 改善(Mobile Dashboard / Analytics と統一)。
//   - loading:skeleton body + 「生成中…」 + shimmer bars(globals.css の
//     ai-insight-generating-orb ではなく、Mobile Dashboard の mobile-ai-skeleton 复用)
//   - ready:AI 真实 summary
//   - fallback:buildInsight 生成的 summary + フォールバック badge
//   - loading / ready / fallback の 3 状態を aiLoading / aiInsight で区別
//
// 2026-09-16 v1.6:AIインサイト 10 分 sessionStorage cache。
//   - key:`mobile.gbp.aiInsight.{locationId}.{period}`
//   - TTL:10 min(Mobile Dashboard と同じ)
//   - 初 mount:cache hit → 即時 ready / miss → fetch → cache write
//   - refresh ボタン click → cache bypass + 再 fetch + cache overwrite

import { useEffect, useState } from "react";
import {
  Edit3,
  ExternalLink,
  Globe,
  MapPin,
  Phone,
  RefreshCw,
  Sparkles,
  WifiOff,
  X,
} from "lucide-react";
import { MobileCard } from "../components/MobileCard";
import { googleBusinessApi } from "../../api/googleBusiness";
import type {
  GbpAiInsight,
  GbpDashboard,
  GbpPerformance,
  GbpSettings,
} from "../../api/googleBusiness";

const GBP_AI_CACHE_TTL_MS = 10 * 60 * 1000;

type TimedCache<T> = { savedAt: number; value: T };

function readTimedCache<T>(key: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TimedCache<T>;
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > GBP_AI_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

function writeTimedCache<T>(key: string, value: T): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
  } catch {
    // sessionStorage unavailable
  }
}

// ---------- PC 版 buildInsight + normalizeAiInsight 复用 ----------
// PC: frontend/src/modules/googleBusiness/GoogleBusinessPage.tsx (lines 133-154 + 278-310)
// 不动 PC → 在 Mobile 内复制(纯函数,无副作用)。
// Mobile 只显示 summary,不显示 insights / actions / questions / chat。
type InsightData = {
  summary: string;
};

/**
 * PC 版 normalizeAiInsight の Mobile コピー。
 * - null / 型違いを除外
 * - summary が空文字以外なら採用
 */
function normalizeAiInsight(value?: GbpAiInsight): InsightData | null {
  if (!value || typeof value.summary !== "string" || !value.summary.trim()) {
    return null;
  }
  return { summary: value.summary };
}

/**
 * PC 版 buildInsight の Mobile コピー(簡略版:summary のみ)。
 * 真 API が失敗 / null 返却時の fallback(performance + dashboard から生成)。
 */
function buildInsight(
  dashboard: GbpDashboard | null,
  performance: GbpPerformance | null,
): InsightData {
  const totals =
    performance?.totals ||
    dashboard?.metrics30d || {
      searches: 0,
      calls: 0,
      websiteClicks: 0,
      directions: 0,
    };
  const aiLines =
    performance?.aiAnalysis.text?.split("\n").filter(Boolean).slice(0, 3) || [];
  const summary =
    aiLines[0] ||
    `直近30日間は検索 ${totals.searches.toLocaleString()}、電話 ${totals.calls.toLocaleString()}、Webサイトクリック ${totals.websiteClicks.toLocaleString()}、経路検索 ${totals.directions.toLocaleString()} です。Google上で見つけてもらう力と、問い合わせに進む導線を分けて確認できます。`;
  return { summary };
}

// ---------- Page ----------

export const MobileGoogleBusinessPage = () => {
  const [settings, setSettings] = useState<GbpSettings | null>(null);
  const [dashboard, setDashboard] = useState<GbpDashboard | null>(null);
  const [performance, setPerformance] = useState<GbpPerformance | null>(null);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  // 真 AIインサイト (API 返却値)。null = 未取得 or 無効
  const [aiInsight, setAiInsight] = useState<InsightData | null>(null);
  // AI fetch 在途フラグ(2026-09-16):skeleton 表示の制御用
  //   aiLoading=true + aiInsight=null → 生成中 skeleton
  //   aiLoading=false + aiInsight=null → fallback 表示
  //   aiLoading=false + aiInsight!=null → AI 表示
  const [aiLoading, setAiLoading] = useState(false);

  // 設定 + location
  useEffect(() => {
    googleBusinessApi.getSettings().then((s) => {
      setSettings(s);
      if (s.connection && s.locations.length > 0) {
        setConnected(true);
        const locId =
          s.currentLocation?.id ?? s.lastSelectedLocationId ?? s.locations[0].id;
        setCurrentLocationId(locId);
      } else {
        setConnected(false);
      }
      setLoading(false);
    }).catch(() => {
      setConnected(false);
      setLoading(false);
    });
  }, []);

  // dashboard + performance 並列拉
  useEffect(() => {
    if (!connected || !currentLocationId) return;
    let cancelled = false;
    Promise.all([
      googleBusinessApi.getDashboard(currentLocationId),
      googleBusinessApi.getPerformance(currentLocationId, 30),
    ])
      .then(([d, p]) => {
        if (cancelled) return;
        setDashboard(d);
        setPerformance(p);
      })
      .catch(() => {
        // 静默失败
      });
    return () => {
      cancelled = true;
    };
  }, [connected, currentLocationId]);

  // 真 AIインサイト取得 — PC 同 API (POST /google-business/ai-insight)
  // dashboard + performance 揃ったら 1 回叩く。失敗 / null なら fallback (buildInsight)
  // 2026-09-16:10 分 sessionStorage cache + loading 状態追跡。
  useEffect(() => {
    if (!connected || !currentLocationId || !dashboard || !performance) {
      setAiInsight(null);
      setAiLoading(false);
      return;
    }
    const period = performance.period || 30;
    const cacheKey = `mobile.gbp.aiInsight.${currentLocationId}.${period}`;
    const cached = readTimedCache<GbpAiInsight>(cacheKey);
    if (cached) {
      setAiInsight(normalizeAiInsight(cached));
      setAiLoading(false);
      return;
    }
    let cancelled = false;
    setAiInsight(null);
    setAiLoading(true);
    googleBusinessApi
      .generateAiInsight({
        locationId: currentLocationId,
        period,
      })
      .then((result) => {
        if (cancelled) return;
        setAiInsight(normalizeAiInsight(result.insight));
        if (result.insight) writeTimedCache(cacheKey, result.insight);
      })
      .catch(() => {
        if (cancelled) return;
        // 不设置 null → fallback 会自动填充 summary
      })
      .finally(() => {
        if (!cancelled) setAiLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connected, currentLocationId, dashboard, performance]);

  // toast 自动消失
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleRefresh = async () => {
    if (!currentLocationId || refreshing) return;
    setRefreshing(true);
    try {
      const [d, p] = await Promise.all([
        googleBusinessApi.getDashboard(currentLocationId, true),
        googleBusinessApi.getPerformance(currentLocationId, 30, true),
      ]);
      setDashboard(d);
      setPerformance(p);
      setAiInsight(null); // 再 fetch
      setToast("更新しました");
    } catch {
      setToast("更新に失敗しました");
    } finally {
      setRefreshing(false);
    }
  };

  // 真値 → fallback の優先順位
  const fallbackInsight = buildInsight(dashboard, performance);
  const insight = aiInsight || fallbackInsight;

  return (
    <>
      {/* AppBar:左空 / 中标题 / 右刷新 */}
      <header className="mobile-appbar">
        <div className="mobile-appbar-left" />
        <h1 className="mobile-appbar-title">Google Business</h1>
        <div className="mobile-appbar-actions">
          {connected && (
            <button
              type="button"
              className="mobile-appbar-icon-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="更新"
              title={refreshing ? "更新中..." : "更新"}
            >
              <RefreshCw
                size={20}
                strokeWidth={1.8}
                className={refreshing ? "spin" : ""}
              />
            </button>
          )}
        </div>
      </header>

      <main className="mobile-content">
        {/* 未接続 */}
        {connected === false && (
          <MobileCard>
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <WifiOff
                size={40}
                strokeWidth={1.4}
                color="var(--mobile-text-light)"
                style={{ marginBottom: 12 }}
              />
              <p
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--mobile-text)",
                  marginBottom: 6,
                }}
              >
                Google Business 未接続
              </p>
              <p style={{ fontSize: 12, color: "var(--mobile-text-sub)" }}>
                PC 側の「Google Business」ページから接続設定を行ってください。
              </p>
            </div>
          </MobileCard>
        )}

        {/* ローディング */}
        {loading && connected !== false && (
          <div
            style={{
              textAlign: "center",
              padding: "32px 0",
              color: "var(--mobile-text-light)",
            }}
          >
            <RefreshCw size={24} strokeWidth={1.8} className="spin" />
            <p style={{ fontSize: 12, marginTop: 8 }}>読み込み中...</p>
          </div>
        )}

        {/* 店舗カード (no chip selector — PC 側で切替) */}
        {dashboard && !loading && (
          <MobileCard>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <p
                style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: "var(--mobile-text)",
                  margin: 0,
                  flex: 1,
                }}
              >
                {dashboard.location.title}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                <span
                  className={`mobile-badge ${
                    dashboard.location.openStatus === "OPEN" ? "green" : "gray"
                  }`}
                >
                  {dashboard.location.openStatus === "OPEN" ? "営業中" : "閉店"}
                </span>
                <button
                  type="button"
                  className="mobile-gbp-edit-btn"
                  onClick={() => setShowEditModal(true)}
                >
                  <Edit3 size={12} strokeWidth={2} />
                  編集
                </button>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginTop: 12,
                borderTop: "1px solid var(--mobile-divider)",
                paddingTop: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <MapPin size={14} strokeWidth={2} color="var(--mobile-text-light)" />
                <span style={{ fontSize: 12, color: "var(--mobile-text-sub)" }}>
                  {dashboard.location.address}
                </span>
              </div>
              {dashboard.location.phone && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Phone size={14} strokeWidth={2} color="var(--mobile-text-light)" />
                  <a
                    href={`tel:${dashboard.location.phone}`}
                    style={{
                      fontSize: 12,
                      color: "var(--mobile-primary)",
                      textDecoration: "none",
                    }}
                  >
                    {dashboard.location.phone}
                  </a>
                </div>
              )}
              {dashboard.location.websiteUri && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Globe size={14} strokeWidth={2} color="var(--mobile-text-light)" />
                  <a
                    href={dashboard.location.websiteUri}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 12,
                      color: "var(--mobile-primary)",
                      textDecoration: "none",
                    }}
                  >
                    {dashboard.location.websiteUri.replace(/^https?:\/\//, "")}
                  </a>
                </div>
              )}
            </div>
          </MobileCard>
        )}

        {/* パフォーマンス KPI (4 指标) */}
        {dashboard && !loading && (
          <div className="mobile-stat-grid">
            {[
              {
                label: "検索表示",
                value: dashboard.metrics30d.searches,
                bg: "#eff6ff",
                color: "#2563eb",
              },
              {
                label: "電話",
                value: dashboard.metrics30d.calls,
                bg: "#dcfce7",
                color: "#16a34a",
              },
              {
                label: "ウェブサイト",
                value: dashboard.metrics30d.websiteClicks,
                bg: "#fef3c7",
                color: "#f59e0b",
              },
              {
                label: "道案内",
                value: dashboard.metrics30d.directions,
                bg: "#fee2e2",
                color: "#ef4444",
              },
            ].map((m) => (
              <div key={m.label} className="mobile-stat-card">
                <div className="mobile-stat-card-header">
                  <div
                    className="mobile-stat-card-icon"
                    style={{ background: m.bg, color: m.color }}
                  >
                    <MapPin size={14} strokeWidth={2} />
                  </div>
                </div>
                <div className="mobile-stat-card-label">{m.label}</div>
                <div>
                  <span className="mobile-stat-card-value" style={{ fontSize: 18 }}>
                    {m.value >= 1000 ? (m.value / 1000).toFixed(1) + "K" : m.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* AIインサイト — 真 API 値 or buildInsight fallback(summary のみ) */}
        {dashboard && !loading && (
          <div className="mobile-gbp-ai-card">
            <div className="mobile-gbp-ai-title">
              <Sparkles size={16} strokeWidth={2} color="#7c3aed" />
              <span>AIインサイト</span>
              {/* loading 中は badge 抑制、loading 完了後 aiInsight が null の場合のみ fallback */}
              {!aiLoading && !aiInsight && (
                <span className="mobile-gbp-ai-badge">フォールバック</span>
              )}
            </div>
            {aiLoading && !aiInsight ? (
              <div className="mobile-ai-skeleton">
                <div className="mobile-ai-skeleton-label">
                  <span className="mobile-ai-skeleton-dot" />
                  生成中…
                </div>
                <div className="mobile-ai-skeleton-bars">
                  <span className="mobile-ai-skeleton-bar" style={{ width: "82%" }} />
                  <span className="mobile-ai-skeleton-bar" style={{ width: "60%" }} />
                  <span className="mobile-ai-skeleton-bar" style={{ width: "44%" }} />
                </div>
              </div>
            ) : (
              <p className="mobile-gbp-ai-summary">{insight.summary}</p>
            )}
          </div>
        )}

        {/* PC 遷移 */}
        {dashboard && !loading && (
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <a
              href="/google-business"
              style={{
                fontSize: 12,
                color: "var(--mobile-primary)",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              PC版 Google Business を見る <ExternalLink size={11} strokeWidth={2} />
            </a>
          </div>
        )}
      </main>

      {/* 編集は PC 版で — modal */}
      {showEditModal && (
        <>
          <div
            className="mobile-gbp-modal-backdrop"
            onClick={() => setShowEditModal(false)}
            aria-hidden="true"
          />
          <div className="mobile-gbp-modal" role="dialog" aria-modal="true">
            <div className="mobile-gbp-modal-head">
              <h3>店舗情報の編集</h3>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                aria-label="閉じる"
                className="mobile-appbar-icon-btn"
                style={{ width: 32, height: 32 }}
              >
                <X size={16} strokeWidth={2} />
              </button>
            </div>
            <div className="mobile-gbp-modal-body">
              <p className="mobile-gbp-modal-line">この機能はPC版でご利用ください。</p>
              <p className="mobile-gbp-modal-line">
                店舗情報の編集・更新は、PC版での操作を推奨しています。
              </p>
            </div>
            <button
              type="button"
              className="mobile-btn mobile-btn-primary mobile-btn-block"
              onClick={() => setShowEditModal(false)}
              style={{ marginTop: 14 }}
            >
              閉じる
            </button>
          </div>
        </>
      )}

      {/* toast */}
      {toast && <div className="mobile-toast">{toast}</div>}
    </>
  );
};